import { useEffect, useState } from "react";
import { api } from "@/lib/api";

// A monetization ad slot. Rotates through all active banners for a placement
// (fetched from the backend), so the same slot can be sold to multiple sponsors.
// Falls back to a clearly-labelled placeholder when none are configured.
export default function AdSlot({ placement, className = "", variant = "banner", interval = 6000 }) {
  const [ads, setAds] = useState(null);
  const [index, setIndex] = useState(0);
  const [fade, setFade] = useState(true);

  useEffect(() => {
    api.get("/ads", { params: { placement } }).then((r) => setAds(r.data)).catch(() => setAds([]));
  }, [placement]);

  useEffect(() => {
    if (!ads || ads.length <= 1) return;
    const t = setInterval(() => {
      setFade(false);
      setTimeout(() => {
        setIndex((i) => (i + 1) % ads.length);
        setFade(true);
      }, 350);
    }, interval);
    return () => clearInterval(t);
  }, [ads, interval]);

  if (ads === null) return null;

  if (ads.length === 0) {
    return (
      <div
        data-testid={`ad-placeholder-${placement}`}
        className={`relative flex items-center justify-center border border-dashed border-border rounded-sm bg-muted/40 text-muted-foreground ${
          variant === "sidebar" ? "min-h-[250px]" : "min-h-[90px]"
        } ${className}`}
      >
        <span className="absolute top-2 left-2 text-[10px] uppercase tracking-[0.2em] font-semibold text-muted-foreground/70">
          Pubblicità
        </span>
        <span className="text-xs">Spazio pubblicitario disponibile</span>
      </div>
    );
  }

  const ad = ads[index % ads.length];

  return (
    <div className={`relative ${className}`} data-testid={`ad-${placement}`}>
      <a
        href={ad.link_url || "#"}
        target="_blank"
        rel="noopener noreferrer sponsored"
        className="relative block overflow-hidden border border-border rounded-sm bg-card group transition-opacity duration-300"
        style={{ opacity: fade ? 1 : 0 }}
        data-testid={`ad-link-${placement}`}
      >
        <span className="absolute top-2 left-2 z-10 text-[10px] uppercase tracking-[0.15em] font-semibold bg-background/80 px-1.5 py-0.5 rounded-sm text-muted-foreground">
          {ad.sponsor ? `Sponsor · ${ad.sponsor}` : "Sponsorizzato"}
        </span>
        {ad.image_url ? (
          <img src={ad.image_url} alt={ad.title} className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform" />
        ) : (
          <div className={`flex items-center justify-center px-4 ${variant === "sidebar" ? "min-h-[250px]" : "min-h-[90px]"}`}>
            <span className="font-serif text-lg font-semibold text-center">{ad.title}</span>
          </div>
        )}
      </a>
      {ads.length > 1 && (
        <div className="absolute bottom-2 right-2 z-10 flex gap-1" data-testid={`ad-dots-${placement}`}>
          {ads.map((a, i) => (
            <button
              key={a.id || i}
              aria-label={`Banner ${i + 1}`}
              onClick={() => { setFade(false); setTimeout(() => { setIndex(i); setFade(true); }, 200); }}
              className={`h-1.5 rounded-full transition-all ${i === index % ads.length ? "w-4 bg-primary" : "w-1.5 bg-muted-foreground/40"}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
